import React from 'react';
import { useOrganizations, useUserAccess } from '../hooks/useOrganizations';
import ProtectedRouteBase from '@shared/routing/ProtectedRoute';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  requireAdmin?: boolean;
  requireOrgAdmin?: boolean;
}

/**
 * Client-side system-admin check (defense-in-depth / UX only — the real
 * boundary is server-side per-endpoint requireSystemAdmin). Matches the
 * `isAdmin` derivation used elsewhere in App.tsx.
 */
const isSystemAdmin = (): boolean => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.role?.toLowerCase() === 'system_admin';
  } catch {
    return false;
  }
};

const ORG_ADMIN_ROLES = ['org_admin', 'system_admin'];

/**
 * Org-admin gate, split into its own component so the membership fetch happens
 * only on routes that ask for it — every other protected route keeps its single
 * /auth/me call.
 *
 * The role derivation matches the Organization Settings menu entry
 * (`getUserRole` in Layout.tsx): org admin in ANY organization, system admins
 * included. Until this existed the menu was the only gate, so the route was
 * reachable by typing the URL.
 */
const OrgAdminRoute: React.FC<{ children: React.ReactNode; requireSubscription: boolean }> = ({
  children,
  requireSubscription,
}) => {
  const { hasSubscriptionAccess, loading: accessLoading } = useUserAccess();
  const { proOrganizations, loading: orgsLoading } = useOrganizations();

  /*
    Fails closed: a failed /organizations/user call leaves `organizations` empty
    and redirects. That is the right default for a gate, and it costs the user
    nothing they could have used — every endpoint behind this route enforces
    requireOrgAdmin server-side, so the page would have 403'd panel by panel.
  */
  /* Pro memberships only. Citizen rows are created with role 'member'
     (auth.js), so this cannot lock anyone out — it just stops a citizen
     membership from ever being read as an admin one. */
  const isOrgAdmin = proOrganizations.some((org) =>
    ORG_ADMIN_ROLES.includes((org.role || '').toLowerCase())
  );

  return (
    <ProtectedRouteBase
      allow={(!requireSubscription || hasSubscriptionAccess) && isOrgAdmin}
      isLoading={accessLoading || orgsLoading}
      redirectTo={!isOrgAdmin ? '/dashboard' : '/restricted'}
      loadingLabel="Checking access permissions"
    >
      {children}
    </ProtectedRouteBase>
  );
};

/**
 * ProtectedRoute component that restricts access based on subscription status
 * and, optionally, role.
 *
 * Usage:
 * <ProtectedRoute requireSubscription={true} requireAdmin={true}>
 *   <AdminNextLayout />
 * </ProtectedRoute>
 *
 * `requireAdmin` means SYSTEM admin; `requireOrgAdmin` means organization admin
 * (system admins included). They are different populations — do not reach for
 * requireAdmin to protect an org-admin surface, or every org admin gets
 * redirected away from it.
 *
 * NOTE: both are UX guards so the wrong user gets a clean redirect instead of a
 * broken page — neither is the security boundary. Admin API endpoints enforce
 * system_admin server-side, and organization endpoints enforce org admin.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSubscription = false,
  requireAdmin = false,
  requireOrgAdmin = false,
}) => {
  /*
    Routes never flip requireOrgAdmin at runtime, so each mount takes one branch
    and keeps it — the differing hook sets below are stable per instance.
  */
  if (requireOrgAdmin) {
    return <OrgAdminRoute requireSubscription={requireSubscription}>{children}</OrgAdminRoute>;
  }

  return (
    <SubscriptionRoute requireSubscription={requireSubscription} requireAdmin={requireAdmin}>
      {children}
    </SubscriptionRoute>
  );
};

/** The original gate: subscription, plus optional system-admin. */
const SubscriptionRoute: React.FC<{
  children: React.ReactNode;
  requireSubscription: boolean;
  requireAdmin: boolean;
}> = ({ children, requireSubscription, requireAdmin }) => {
  const { hasSubscriptionAccess, loading } = useUserAccess();
  const adminOk = !requireAdmin || isSystemAdmin();

  return (
    <ProtectedRouteBase
      allow={(!requireSubscription || hasSubscriptionAccess) && adminOk}
      isLoading={loading}
      redirectTo={!adminOk ? '/dashboard' : '/restricted'}
      loadingLabel="Checking access permissions"
    >
      {children}
    </ProtectedRouteBase>
  );
};

export default ProtectedRoute;
